import React, { useState } from 'react';
import { Formik, Form } from 'formik';
import KontaktdatenHV from './forms/_kontaktdatenHV';
import Reklamation from './forms/_reklamation';
import Dringlichkeit from './forms/_dringlichkeit';
import Terminierung from './forms/_terminierung';
import { Container, Box } from '@mui/material';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import { Button } from '@mui/base/Button';
import Grid from '@mui/material/Grid';
import { useNavigate } from 'react-router-dom';
import Hidden from '@mui/material/Hidden';
import Alert from '@mui/material/Alert';
import CheckIcon from '@mui/icons-material/Check';
import dayjs from 'dayjs';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import SharePointService from '../services/sharepoint.service';

// Hausverwaltungs-Variante der Reklamationserfassung. Im Unterschied zu
// warranty.js: Kontaktdaten werden nicht aus dem eingeloggten Mieter-Profil
// vorbefüllt, sondern von der Hausverwaltung frei für den jeweiligen Mieter
// eingetragen (aktuell mit Dummy-Dropdowns, siehe _kontaktdatenHV.js).
const WarrantyHausverwaltung = () => {
  const steps = ['Kontaktdaten', 'Reklamation', 'Dringlichkeit', 'Terminierung'];
  const [step, setStep] = useState(0);
  const totalSteps = steps.length;
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isFormValid, setIsFormValid] = useState(false);
  const [isFormsuccess, setIsFormSuccess] = useState(false);
  const [formValues, setFormValues] = useState({
    name: '',
    email: '',
    tel: '',
    whg: '',
    address: '',
    project: '',
    raum: '',
    details: '',
    files: [],
    dringlichkeit: '',
    dringlichkeitDAYS: 0,
    termin1: '',
    termin2: '',
    termin3: '',
  });

  const prevStep = () => setStep((s) => Math.max(s - 1, 0));
  const nextStep = () => {
    setIsFormValid(false);
    setStep((s) => Math.min(s + 1, totalSteps - 1));
  };

  const navigate = useNavigate();
  const navigateToDashboard = () => {
    navigate('/dashboard');
    window.location.reload();
  };

  const handleSubmit = async (values) => {
    setIsSubmitting(true);

    try {
      await SharePointService.createWarranty(values);
      setIsFormSuccess(true);
      setTimeout(() => navigateToDashboard(), 4000);
    } catch (error) {
      console.error('Fehler beim Senden:', error);
      // Optional: Fehlerbehandlung
    } finally {
      setIsSubmitting(false);
    }
  };

  const isWeekend = (date) => {
    const day = dayjs(date).day();
    return day === 0 || day === 6;
  };

  const validateForm = (values) => {
    let errors = {};
    if (step === 0) {
      if (!values.name || !values.email || !values.tel || values.whg === '' || !values.address || values.project === '') {
        errors = {
          ...errors,
          name: !values.name ? 'Erforderlich' : undefined,
          email: !values.email ? 'Erforderlich' : undefined,
          tel: !values.tel ? 'Erforderlich' : undefined,
          whg: values.whg === '' ? 'Erforderlich' : undefined,
          address: !values.address ? 'Erforderlich' : undefined,
          project: values.project === '' ? 'Erforderlich' : undefined,
        };
      }
    } else if (step === 1) {
      if (!values.raum) errors.raum = 'Erforderlich';
      if (!values.details) errors.details = 'Erforderlich';
      if (!values.magelart) errors.magelart = 'Erforderlich';
    } else if (step === 2) {
      if (values.dringlichkeit === undefined || values.dringlichkeit === null) {
        errors.dringlichkeit = 'Erforderlich';
      }
    } else if (step === 3) {
      const check = (key, minDays, msg) => {
        if (!values[key]) return (errors[key] = 'Erforderlich');
        if (isWeekend(values[key])) return (errors[key] = `${msg} kann nicht an einem Wochenende sein`);
        const diff = dayjs(values[key], 'DD.MM.YYYY - HH:mm').startOf('day').diff(dayjs().startOf('day'), 'day');
        if (diff < minDays) errors[key] = `${msg} muss mindestens ${minDays} Tage in der Zukunft liegen`;
      };
      const minFor = (d) => (d === 'Schönheitsreparatur' ? 14 : d === 'Eingeschränkte Funktion' ? 7 : 0);
      check('termin1', minFor(values.dringlichkeit), 'Terminvorschlag 1');
      check('termin2', minFor(values.dringlichkeit), 'Terminvorschlag 2');
      check('termin3', minFor(values.dringlichkeit), 'Terminvorschlag 3');
    }
    setIsFormValid(Object.keys(errors).length === 0);
    return errors;
  };

  const success = (
    <Alert icon={<CheckIcon fontSize="inherit" />} severity="success">
      Das Formular wurde erfolgreich abgeschickt. Vielen Dank!
    </Alert>
  );

  // Responsive Buttons (volle Breite auf Handy/Tablet)
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box sx={{ background: '#F0F2F5', py: 5 }}>
        <Container maxWidth="xl">
      {/* Dark Stepper-Bar mit responsivem Padding */}
      <Container maxWidth="xl" sx={{ background: '#1B1E21', px: { xs: 2, md: 6 }, py: { xs: 2, md: 6 } }}>
        <Grid container>
          <Grid item xs={12}>
            <Hidden smDown>
              <Stepper activeStep={step}>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Hidden>
            <Hidden smUp>
              <Stepper activeStep={step} orientation="vertical">
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Hidden>
          </Grid>
        </Grid>
      </Container>

      {/* Form-Container */}
      <Container maxWidth="xl" sx={{ background: 'white', pt: 4, px: { xs: 2, md: 6 }, pb: { xs: 2, md: 4 } }}>
        <Formik initialValues={formValues} onSubmit={handleSubmit} validate={validateForm}>
          {(formikProps) => (
            <Form encType="multipart/form-data">
              {step === 0 && <KontaktdatenHV formikProps={formikProps} setFormValues={setFormValues} />}
              {step === 1 && <Reklamation formikProps={formikProps} setFormValues={setFormValues} />}
              {step === 2 && <Dringlichkeit formikProps={formikProps} setFormValues={setFormValues} />}
              {step === 3 && <Terminierung formikProps={formikProps} setFormValues={setFormValues} />}

              {/* Actions: stacked auf smDown */}
              <Box
                py={5}
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 2,
                  justifyContent: 'space-between',
                }}
              >
                {step > 0 && (
                  <Button
                    className="btn sec"
                    type="button"
                    onClick={prevStep}
                    style={{ width: isSmDown ? '100%' : undefined }}
                  >
                    Zurück
                  </Button>
                )}

                {step < totalSteps - 1 && (
                  <Button
                    type="button"
                    className="btn prim"
                    onClick={nextStep}
                    disabled={!isFormValid}
                    style={{ width: isSmDown ? '100%' : undefined }}
                  >
                    Weiter
                  </Button>
                )}

                {step === totalSteps - 1 && (
                  <Button
                    type="submit"
                    className="btn prim"
                    disabled={!isFormValid || isSubmitting}
                    style={{ width: isSmDown ? '100%' : undefined }}
                  >
                    {isSubmitting ? (
                      <CircularProgress size={20} sx={{ color: 'white' }} />
                    ) : (
                      'Absenden'
                    )}
                  </Button>
                )}
              </Box>
            </Form>
          )}
        </Formik>

        <Box pb={4}>{isFormsuccess ? success : ''}</Box>
      </Container>
      </Container>
    </Box>
  );
};

export default WarrantyHausverwaltung;